import { Link } from "react-router-dom";
import { Wordmark } from "@/components/Logo";

const EFFECTIVE_DATE = "June 15, 2026";

const SECTIONS = [
  {
    h: "1. Acceptance of terms",
    p: "By creating an account or using RollOSS, you agree to be bound by these Terms of Service. If you do not agree, do not use the service.",
  },
  {
    h: "2. What RollOSS is",
    p: "RollOSS is a Software-as-a-Service product that helps Brazilian Jiu-Jitsu academies run their schedule, "
       + "roster, attendance, belt progression, announcements and a mobile student companion app. We provide the "
       + "platform; the academy is responsible for the accuracy of the information they enter and the conduct of their students.",
  },
  {
    h: "3. Eligibility and accounts",
    items: [
      "You must be at least 18 years old to be an academy owner or coach.",
      "Students under 18 may use the student app with the consent of a parent or guardian and the academy.",
      "You are responsible for keeping your sign-in credentials secure. We use Google sign-in to reduce that burden.",
      "You must provide accurate information and update it when it changes.",
    ],
  },
  {
    h: "4. Subscription plans and billing",
    items: [
      "RollOSS offers three subscription tiers: Tatame, Academy, and Federation. Pricing is published at getrolloss.app.",
      "Plans are billed in advance, monthly or annually. Annual plans include a discount.",
      "We offer a free trial. No card is required during the trial. We will not begin charging you until you choose a paid plan.",
      "If we wire up automatic billing, we use Stripe as our payment processor. We never see or store your card number.",
      "Subscriptions renew automatically until cancelled. You may cancel at any time from your academy settings or by emailing info@getrolloss.app.",
      "Refunds are evaluated case by case. Cancellation stops future charges; it does not refund the current period unless required by law.",
    ],
  },
  {
    h: "5. Acceptable use",
    p: "You agree not to:",
    items: [
      "Use RollOSS to send spam, harass anyone, or violate any law.",
      "Attempt to reverse engineer, decompile, or otherwise reconstruct our software.",
      "Use the service to store sensitive data we did not design it for (e.g., medical records, government IDs, payment data outside of our billing).",
      "Resell access to the service or share an owner account between multiple academies that have not been authorized in writing.",
      "Bulk-scrape data or use automated tools to overload our infrastructure.",
    ],
  },
  {
    h: "6. Your content and your students",
    p: "You retain ownership of the data you put into RollOSS (your schedule, your roster, your attendance, your "
       + "announcements). You grant RollOSS a worldwide, non-exclusive license to host and display this content for "
       + "the sole purpose of running the service for you. If you delete an academy, we delete its data within 30 days.",
  },
  {
    h: "7. Push notifications, email, and SMS",
    p: "RollOSS sends transactional emails (invitations, password resets, security notices) and, if you opt in, "
       + "push notifications and class reminders. You can disable push notifications at any time from your device "
       + "settings or from your in-app notification preferences. Transactional emails are part of operating your account "
       + "and cannot be disabled without closing the account.",
  },
  {
    h: "8. Availability",
    p: "We work hard to keep RollOSS available 24/7 but we do not guarantee uninterrupted service. Planned maintenance, "
       + "vendor outages, and force majeure events can interrupt access. We will not be liable for downtime; our liability "
       + "is limited as described in Section 11.",
  },
  {
    h: "9. Termination",
    p: "You may close your account at any time. We may suspend or terminate your account if you violate these terms, "
       + "if a payment is overdue by more than 30 days, or if required by law. We will give you at least 14 days notice "
       + "before deleting your data unless we are required to act sooner.",
  },
  {
    h: "10. Disclaimers",
    p: "RollOSS is provided \u201Cas is\u201D and \u201Cas available.\u201D We disclaim all warranties not expressly stated "
       + "in these terms, including any implied warranty of merchantability, fitness for a particular purpose, or "
       + "non-infringement. We are not responsible for injuries that occur on the mats; you operate an academy at your own risk.",
  },
  {
    h: "11. Limitation of liability",
    p: "To the maximum extent permitted by law, RollOSS\u2019 total liability for any claim arising out of or relating "
       + "to the service is limited to the greater of (a) one hundred US dollars (US$100) or (b) the amount you paid "
       + "to RollOSS in the twelve months preceding the claim. We are not liable for indirect, incidental, special, "
       + "consequential, or punitive damages.",
  },
  {
    h: "12. Governing law and disputes",
    p: "These terms are governed by the laws of Panama, without regard to conflict-of-law principles. Any dispute will "
       + "first be addressed by good-faith negotiation. If that fails, it will be resolved in the courts of Panama City, Panama.",
  },
  {
    h: "13. Changes to these terms",
    p: "We may update these terms from time to time. For material changes we will notify account owners by email and "
       + "post the updated terms here with a new effective date. Continued use after the effective date constitutes acceptance.",
  },
  {
    h: "14. Contact",
    p: "Questions about these terms? Email info@getrolloss.app.",
  },
];

export default function Terms() {
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh", color: "var(--text)" }}>
      <header className="px-6 md:px-8 py-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border)" }}>
        <Link to="/" data-testid="logo-link"><Wordmark /></Link>
        <Link to="/" className="label-tiny hover:text-white" data-testid="back-home">← Back to home</Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="label" style={{ color: "var(--green)" }}>LEGAL</div>
        <h1 className="text-4xl md:text-5xl font-bold uppercase tracking-tight mt-2">Terms of Service</h1>
        <div className="label-tiny mt-2" style={{ color: "var(--text-dim)" }}>Effective {EFFECTIVE_DATE}</div>

        <p className="mt-8 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>
          These Terms govern your use of RollOSS. We have tried to keep them readable. If anything is unclear, email <a href="mailto:info@getrolloss.app" style={{ color: "var(--green)" }}>info@getrolloss.app</a>.
        </p>

        <div className="mt-10 space-y-8" data-testid="terms-sections">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="text-[18px] font-bold uppercase tracking-tight">{s.h}</h2>
              {s.p && <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "var(--text-dim)" }}>{s.p}</p>}
              {s.items && (
                <ul className="mt-3 space-y-1.5">
                  {s.items.map((it) => (
                    <li key={it} className="text-[14px] leading-relaxed pl-5" style={{ color: "var(--text-dim)", position: "relative" }}>
                      <span style={{ position: "absolute", left: 0, color: "var(--green)" }}>•</span>{it}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-16 pt-8 border-t flex flex-wrap gap-6 text-[13px]" style={{ borderColor: "var(--border)", color: "var(--text-dim)" }}>
          <Link to="/privacy" className="hover:text-white" data-testid="link-privacy">Privacy Policy</Link>
          <Link to="/" className="hover:text-white">getrolloss.app</Link>
          <a href="mailto:info@getrolloss.app" className="hover:text-white">info@getrolloss.app</a>
        </div>
      </main>
    </div>
  );
}
